import { Mail, Phone } from 'lucide-react';
import { ROUTE_NAMES, APP_NAME } from '@tsgecart/config';
import { Seo } from '../components/seo.js';
import { useContactInfo } from '../features/storefront/contact/hooks/use-contact.js';
import { ContactForm } from '../features/storefront/contact/components/contact-form.js';
import { StorefrontSkeleton } from '../features/storefront/components/states.js';

/**
 * Support email/phone are read live from `GET /api/v1/contact`, which
 * surfaces the store's own `store_profile.supportEmail`/`supportPhone`
 * (already-real admin-configured data, see
 * apps/worker/src/modules/platform/settings/repositories/store-profile-repository.ts)
 * -- never a hardcoded/fabricated phone number. If the store profile has
 * no support email/phone configured yet, that field is simply omitted
 * rather than showing a placeholder that looks real.
 */
export function ContactPage() {
  const { data, isLoading } = useContactInfo();

  return (
    <>
      <Seo
        title="Contact us"
        description={`Get in touch with ${APP_NAME} -- support email, phone and a contact form for order or delivery questions.`}
        canonicalPath={ROUTE_NAMES.contact}
        ogType="website"
      />

      <section className="mx-auto max-w-4xl px-4 py-12 text-center sm:px-8 sm:py-16">
        <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl">Contact us</h1>
        <p className="mx-auto mt-4 max-w-2xl text-base text-gray-600">
          Questions about an order, delivery area, or anything else? Reach us directly or send a message below.
        </p>
      </section>

      <section className="mx-auto grid max-w-4xl grid-cols-1 gap-6 px-4 pb-14 sm:px-8 md:grid-cols-[240px_1fr]">
        <div className="space-y-3">
          {isLoading ? (
            <>
              <StorefrontSkeleton className="h-16 w-full" />
              <StorefrontSkeleton className="h-16 w-full" />
            </>
          ) : (
            <>
              {data?.supportEmail && (
                <a
                  href={`mailto:${data.supportEmail}`}
                  className="focus-visible:ring-brand flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2"
                >
                  <Mail className="text-brand-foreground h-5 w-5 shrink-0" aria-hidden="true" />
                  <span>
                    <span className="block text-xs font-medium text-gray-500">Email</span>
                    <span className="block text-sm font-semibold text-gray-900">{data.supportEmail}</span>
                  </span>
                </a>
              )}
              {data?.supportPhone && (
                <a
                  href={`tel:${data.supportPhone}`}
                  className="focus-visible:ring-brand flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2"
                >
                  <Phone className="text-brand-foreground h-5 w-5 shrink-0" aria-hidden="true" />
                  <span>
                    <span className="block text-xs font-medium text-gray-500">Phone</span>
                    <span className="block text-sm font-semibold text-gray-900">{data.supportPhone}</span>
                  </span>
                </a>
              )}
              {!data?.supportEmail && !data?.supportPhone && (
                <p className="rounded-2xl border border-dashed border-gray-200 bg-white p-4 text-sm text-gray-500">
                  Support contact details aren&apos;t configured yet -- use the form to reach us.
                </p>
              )}
            </>
          )}
        </div>

        <ContactForm />
      </section>
    </>
  );
}
